import { useState, useEffect, useCallback, useRef } from 'react';
import { useWebRTC } from '../context/WebRTCContext';

// Self-heal cadence: even with incremental pushes, re-pull the authoritative
// pending set periodically (and on tab focus) so a missed/never-sent event
// can't leave a surface permanently drifted. The badge used to climb forever
// because TTL expiry sent no push and nothing ever re-synced it.
const RESYNC_MS = 30_000;

/**
 * useAdvisorQueueState — the stateful React binding for the AI Advisor Action
 * Queue. Prefer the shared `useAdvisorQueue` from AdvisorQueueContext in UI
 * code: this hook holds its OWN copy of the queue, so calling it in more than
 * one mounted component gives each an independent (and drift-prone) count. The
 * provider instantiates this exactly once for the whole app.
 *
 * In Advisor mode the AI proposes actions instead of taking them. This surfaces
 * the live pending proposals and the approve/dismiss controls. The desktop
 * AdvisorService is the source of truth; we load the current pending set on
 * connect, apply incremental advisor.* pushes, and periodically re-sync.
 *
 * @returns {{ pending, count, approve, dismiss, refresh, busyIds }}
 */
export function useAdvisorQueueState() {
    const { isConnected, featureManager } = useWebRTC();
    const [pending, setPending] = useState([]);
    const [busyIds, setBusyIds] = useState({}); // id -> 'approving' | 'dismissing'
    // Transient execution outcome for the last approved action:
    //   { kind: 'success' | 'error', message, ts }
    const [feedback, setFeedback] = useState(null);
    // droppedByCap: how many lower-priority proposals the desktop evicted to
    // stay under the queue cap (so the count stops implying nothing was lost).
    const [droppedByCap, setDroppedByCap] = useState(0);
    const featureRef = useRef(null);

    const clearFeedback = useCallback(() => setFeedback(null), []);

    const refresh = useCallback(async () => {
        const f = featureManager?.getFeature?.('sacred-timeline');
        if (!f?.listAdvisorPending) return;
        try {
            const res = await f.listAdvisorPending(100);
            // The list may arrive as a bare array or wrapped with cap metadata.
            const list = Array.isArray(res) ? res : (res?.proposals || res?.pending || res?.items || []);
            setPending(list);
            const dropped = res?.droppedByCap ?? res?.meta?.droppedByCap;
            if (dropped != null) setDroppedByCap(dropped);
        } catch (err) {
            console.warn('[useAdvisorQueue] refresh failed:', err.message);
        }
    }, [featureManager]);

    useEffect(() => {
        if (!isConnected || !featureManager) return;
        const f = featureManager.getFeature('sacred-timeline');
        if (!f) return;
        featureRef.current = f;
        refresh();

        const unsub = f.onAdvisorEvent?.((type, proposal) => {
            if (!proposal || proposal.id == null) return;

            // Cap-eviction metadata can ride along on any pending-list push.
            const dropped = proposal.droppedByCap ?? proposal.meta?.droppedByCap;
            if (dropped != null) setDroppedByCap(dropped);

            if (type === 'advisor.executed') {
                // Contract: outcome lives on result.{status,message} or the
                // payload itself. Surface success/failure instead of silently
                // deleting the card.
                const status = proposal.result?.status ?? proposal.status ?? 'executed';
                const message = proposal.result?.message ?? proposal.message ?? null;
                const title = proposal.title || proposal.action || 'the action';
                if (status === 'failed') {
                    setFeedback({ kind: 'error', message: message ? `Couldn't run ${title}: ${message}` : `Couldn't run ${title}`, ts: Date.now() });
                } else {
                    setFeedback({ kind: 'success', message: message || `Ran ${title}`, ts: Date.now() });
                }
                setPending((prev) => prev.filter((p) => p.id !== proposal.id));
                return;
            }

            setPending((prev) => {
                if (type === 'advisor.proposed') {
                    if (prev.some((p) => p.id === proposal.id)) return prev;
                    return [proposal, ...prev];
                }
                // Every other terminal signal — advisor.dismissed, advisor.expired
                // (TTL sweep), advisor.capped (inbox overflow) and advisor.reverted
                // — just drops the card silently (no toast; it wasn't the operator's
                // click). advisor.expired is what keeps this in step with the desktop.
                return prev.filter((p) => p.id !== proposal.id);
            });
        });

        return () => unsub?.();
    }, [isConnected, featureManager, refresh]);

    // Self-heal: re-pull the authoritative pending set on an interval and when
    // the tab regains focus. Incremental pushes can be missed (a surface mounts
    // after a proposal was made) or never sent (silent expiry pre-fix), so a
    // periodic re-sync guarantees the count converges on the desktop's truth.
    useEffect(() => {
        if (!isConnected || !featureManager) return;
        const id = setInterval(refresh, RESYNC_MS);
        const onFocus = () => { if (!document.hidden) refresh(); };
        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onFocus);
        return () => {
            clearInterval(id);
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onFocus);
        };
    }, [isConnected, featureManager, refresh]);

    const _decide = useCallback(async (id, kind, arg) => {
        const f = featureRef.current;
        if (!f) return;
        setBusyIds((b) => ({ ...b, [id]: kind === 'approve' ? 'approving' : 'dismissing' }));
        // Optimistic removal — the advisor.* push will confirm.
        setPending((prev) => prev.filter((p) => p.id !== id));
        try {
            if (kind === 'approve') await f.approveAdvisor(id, arg || null);
            else await f.dismissAdvisor(id, arg || null); // arg = feedback {reason, note}
        } catch (err) {
            console.warn(`[useAdvisorQueue] ${kind} failed:`, err.message);
            if (kind === 'approve') {
                setFeedback({ kind: 'error', message: `Couldn't send for approval: ${err.message || 'unknown error'}`, ts: Date.now() });
            }
            refresh(); // re-sync on failure (restores the card)
        } finally {
            setBusyIds((b) => {
                const next = { ...b };
                delete next[id];
                return next;
            });
        }
    }, [refresh]);

    const approve = useCallback((id, editedParams = null) => _decide(id, 'approve', editedParams), [_decide]);
    const dismiss = useCallback((id, feedback = null) => _decide(id, 'dismiss', feedback), [_decide]);

    return { pending, count: pending.length, approve, dismiss, refresh, busyIds, feedback, clearFeedback, droppedByCap };
}
